export type SeoMetric = 'position' | 'impressions' | 'clicks';
export type SeoScale = 'row' | 'common';

export interface SeoCell {
    clicks: number;
    impressions: number;
    ctr: number;
    position: number | null;
    unreliable?: boolean;
}

export interface SeoWindow {
    clicks?: number;
    impressions?: number;
    position?: number | null;
    weeks?: number;
    clicks_week?: number | null;
    impressions_week?: number | null;
    unreliable?: boolean;
}

export interface SeoChange {
    clicks: number | null;
    impressions: number | null;
    position: number | null;
}

export interface SeoTag {
    kind: string;
    date: string;
}

export interface SeoRow {
    path?: string;
    label: string;
    tag?: SeoTag | null;
    resto?: boolean;
    resto_count?: number;
    cells: (SeoCell | null)[];
    before: SeoWindow | null;
    after: SeoWindow | null;
    change: SeoChange | null;
}

export interface SeoGroup {
    id: string;
    label: string;
    rows: SeoRow[];
    summary: SeoRow;
}

export interface SeoBlock {
    id: string;
    name: string;
    hidden: number;
    rows: SeoRow[];
    summary: SeoRow;
    groups?: SeoGroup[];
}

export interface SeoCatalog {
    blogTypes: Record<string, string>;
    storeBrands: Record<string, string>;
}

export interface SeoCard {
    id: string;
    label: string;
    before: number | null;
    after: number | null;
    delta: number | null;
    decimals: number;
    before_txt: string;
    after_txt: string;
    delta_txt: string;
    hint: string;
}

export interface SeoWeek {
    id: string;
    start: string;
    end: string;
    hitos?: string[];
}

export interface SeoSeasonPoint {
    id: string;
    clicks: number | null;
    position: number | null;
    yoy_clicks: number | null;
    yoy_position: number | null;
}

export interface SeoEdge {
    query: string;
    path: string;
    label: string;
    weeks: number;
    clicks: number;
    impressions: number;
    position: number;
}

export interface SeoZeroClick {
    path: string;
    label: string;
    weeks: number;
    impressions: number;
}

export interface SeoCannibalPage {
    path: string;
    label: string;
}

export interface SeoCannibal {
    query: string;
    week: string;
    impressions: number;
    pages: SeoCannibalPage[];
    weeks?: number;
}

export interface SeoReport {
    grain: 'week' | 'day';
    site: string;
    name: string;
    property: string;
    colors: { navy: string; cyan: string; petrol: string };
    data_through: string;
    generated_at: string;
    period: { start: string; end: string };
    compare: {
        ready: boolean;
        consolidated: boolean;
        before_label: string;
        after_label: string;
        before_weeks: number;
        after_weeks: number;
    };
    weeks: SeoWeek[];
    cards: SeoCard[];
    season: SeoSeasonPoint[];
    launch_index: number | null;
    blocks: SeoBlock[];
    unclassified: {
        path: string;
        label: string;
        suggestion: string;
        url_count: number;
        impressions: number;
    }[];
    opportunities: {
        edge: SeoEdge[];
        zero_clicks: SeoZeroClick[];
        cannibal: SeoCannibal[];
    };
    findings: { worked: string[]; flat: string[]; care: string[] };
    lines: string[];
    reconciliation: { week: string; page_clicks: number; site_clicks: number; diff: number; empty?: boolean }[];
    urls_per_week: Record<string, number>;
    empty_weeks: string[];
    truncated: boolean;
    queries_truncated?: boolean;
    impression_floor: number;
}

export interface SeoSeriesWeek {
    id: string;
    start: string;
    end: string;
    clicks: number;
    impressions: number;
    position: number | null;
    yoy_clicks: number | null;
    yoy_impressions: number | null;
    yoy_position: number | null;
}

export interface SeoSeriesPage {
    week_id: string;
    path: string;
    clicks: number;
    impressions: number;
    position: number | null;
}

export interface SeoSeriesQuery {
    week_id: string;
    query: string;
    path: string;
    clicks: number;
    impressions: number;
    position: number | null;
}

/** Filas guardadas en Postgres. El panel arma el mapa a partir de esto. */
export interface SeoSeries {
    data_through: string | null;
    finished_at: string | null;
    truncated: boolean;
    queries_truncated: boolean;
    weeks: SeoSeriesWeek[];
    pages: SeoSeriesPage[];
    queries: SeoSeriesQuery[];
}

export function isSeoSeries(value: unknown): value is SeoSeries {
    if (!value || typeof value !== 'object') return false;
    const series = value as Partial<SeoSeries>;
    return Array.isArray(series.weeks) && Array.isArray(series.pages) && Array.isArray(series.queries);
}

export function isSeoReport(value: unknown): value is SeoReport {
    if (!value || typeof value !== 'object') return false;
    const report = value as Partial<SeoReport>;
    return Array.isArray(report.blocks) && Array.isArray(report.weeks) && Array.isArray(report.cards);
}
